import type { BaseComponentProps, SectionStyle } from '../../types/puck';
import { Facebook, Instagram, Linkedin, MapPin, Phone, Twitter } from "lucide-react";
import { formatPhone, phoneHref } from "@servicepro/utils";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import { BRAND } from "../../brand.js";
import { Button } from "../ui/button";
import React from "react";
import { Separator } from "../ui/separator";
import { cn } from "../../lib/utils";
import { resolveCopyrightYear } from "../../lib/copyright-year";

interface FooterLink {
  label: string;
  linkType: "page" | "url" | "anchor";
  pageId?: string;
  url?: string;
}

interface BusinessHours {
  day: string;
  hours: string;
  closed?: boolean;
}

interface FooterProps extends BaseComponentProps {
  logo?: string;
  companyName: string;
  /** Controls logo/text display: "logo" = logo only (text fallback), "text" = text only, "both" = show both */
  logoDisplay?: "logo" | "text" | "both";
  description: string;
  linkColumns: {
    title: string;
    links: FooterLink[];
  }[];
  showSocial: "yes" | "no";
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  showContact: "yes" | "no";
  phone?: string;
  address?: string;
  showBusinessHours?: "yes" | "no";
  businessHours?: BusinessHours[];
  copyrightText: string;
  style: "simple" | "multi-column" | "centered";
  /** Bold: a dark band in the theme's primary that closes the page with the bold contact section above it. */
  variant?: SectionStyle;
  /** Render the "Powered by ServicePro.co" badge. Forced on for default-plan
   *  sites by the metadata resolver — see resolveSiteBusinessMetadata. */
  showServiceProBadge?: boolean;
  /** The site's business, so a signup from the badge credits the site it came from. */
  businessId?: string;
}

/**
 * The badge's link, tagged with UTMs the signup attribution cookie reads (it ignores `?ref=`).
 * @param {string} [businessId] The site's business, sent as utm_content; omitted when unknown.
 * @returns {string} The servicepro.co URL the badge opens.
 */
const serviceProBadgeHref = (businessId?: string): string => {
  const params = new URLSearchParams({
    utm_source: "jobsite",
    utm_medium: "referral",
    utm_campaign: "powered_by",
  });
  if (businessId !== undefined && businessId !== "") {
    params.set("utm_content", businessId);
  }
  return `https://servicepro.co/?${params.toString()}`;
};

const Footer: React.FC<FooterProps> = ({
  logo,
  companyName,
  logoDisplay = "logo",
  description,
  linkColumns,
  showSocial,
  socialLinks,
  showContact,
  phone,
  address,
  showBusinessHours,
  businessHours,
  copyrightText,
  style,
  variant = "standard",
  showServiceProBadge = true,
  businessId,
  id,
  className,
}) => {
  // Determine what to show based on logoDisplay setting
  const isBold = variant === "bold",
    /**
     * A class list for the footer's usual light surface, or its bold twin on the dark band.
     * @param {string} standard The classes on the light surface.
     * @param {string} bold The classes on the dark band.
     * @returns {string} The classes for this footer.
     */
    ink = (standard: string, bold: string): string => (isBold ? bold : standard),
    socialButtonClass = ink(
      "jobsite:w-10 jobsite:h-10 jobsite:rounded-full",
      "jobsite:w-10 jobsite:h-10 jobsite:rounded-full jobsite:bg-primary-foreground/10 jobsite:text-primary-foreground jobsite:shadow-none jobsite:hover:bg-primary-foreground/20 jobsite:hover:shadow-none",
    ),
    showLogo = logo && logoDisplay !== "text",
    showCompanyName = logoDisplay === "text" || logoDisplay === "both" || !logo,
    // Helper function to resolve link URLs - simplified without usePages
    getUrl = (link: FooterLink) => link.url || "#",

    SocialIcons = () => {
      if (showSocial !== "yes" || !socialLinks) {
        return null;
      }

      return (
        <div className="jobsite:flex jobsite:gap-3">
          {socialLinks.facebook && (
            <Button
              size="icon"
              variant="default"
              className={socialButtonClass}
              asChild
            >
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook className="jobsite:w-5 jobsite:h-5" />
              </a>
            </Button>
          )}
          {socialLinks.twitter && (
            <Button
              size="icon"
              variant="default"
              className={socialButtonClass}
              asChild
            >
              <a
                href={socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <Twitter className="jobsite:w-5 jobsite:h-5" />
              </a>
            </Button>
          )}
          {socialLinks.linkedin && (
            <Button
              size="icon"
              variant="default"
              className={socialButtonClass}
              asChild
            >
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin className="jobsite:w-5 jobsite:h-5" />
              </a>
            </Button>
          )}
          {socialLinks.instagram && (
            <Button
              size="icon"
              variant="default"
              className={socialButtonClass}
              asChild
            >
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram className="jobsite:w-5 jobsite:h-5" />
              </a>
            </Button>
          )}
        </div>
      );
    },

    getFooterLayout = () => {
      if (style === "centered") {
        return (
          <div className="jobsite:text-center jobsite:max-w-3xl jobsite:mx-auto">
            {showLogo && (
              <img
                src={logo}
                alt={companyName}
                crossOrigin="anonymous"
                className="jobsite:h-12 jobsite:w-auto jobsite:object-contain jobsite:mx-auto jobsite:mb-4"
              />
            )}
            {showCompanyName && (
              <h3 className={ink("jobsite:text-2xl jobsite:mb-4 jobsite:text-primary jobsite:font-heading", "jobsite:text-2xl jobsite:mb-3 jobsite:leading-tight jobsite:uppercase jobsite:text-primary-foreground jobsite:font-heading")}>
                {companyName}
              </h3>
            )}
            <p className={ink("jobsite:mb-6 jobsite:text-lg jobsite:text-foreground/80", "jobsite:mb-6 jobsite:text-lg jobsite:text-primary-foreground/70")}>{description}</p>

            {showContact === "yes" && (phone || address) && (
              <div className="jobsite:mb-6 jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3">
                {phone && (
                  <a
                    href={phoneHref(phone)}
                    className={ink("jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:text-primary jobsite:font-semibold jobsite:hover:underline", "jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:text-accent jobsite:font-semibold jobsite:hover:underline")}
                  >
                    <Phone className="jobsite:w-4 jobsite:h-4" />
                    {formatPhone(phone)}
                  </a>
                )}
                {address && (
                  <p className={ink("jobsite:inline-flex jobsite:items-start jobsite:gap-2 jobsite:text-foreground/70 jobsite:whitespace-pre-line", "jobsite:inline-flex jobsite:items-start jobsite:gap-2 jobsite:text-primary-foreground/70 jobsite:whitespace-pre-line")}>
                    <MapPin className="jobsite:w-4 jobsite:h-4 jobsite:mt-0.5 jobsite:shrink-0" />
                    <span>{address}</span>
                  </p>
                )}
              </div>
            )}

            {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
              <div className="jobsite:mb-6">
                <h4 className={ink(" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-3 jobsite:text-muted-foreground jobsite:font-heading", "jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-3 jobsite:text-primary-foreground/60 jobsite:font-heading")}>
                  Business Hours
                </h4>
                <div className={ink("jobsite:space-y-2 jobsite:text-sm jobsite:text-foreground", "jobsite:space-y-2 jobsite:text-sm jobsite:text-primary-foreground/85")}>
                  {businessHours.map((schedule, idx) => (
                    <div key={idx} className="jobsite:flex jobsite:justify-center jobsite:gap-4">
                      <span className="jobsite:font-medium jobsite:w-24 jobsite:text-right">
                        {schedule.day}
                      </span>
                      <span
                        className={cn(
                          "jobsite:w-32 jobsite:text-left",
                          schedule.closed && ink("jobsite:text-muted-foreground jobsite:italic", "jobsite:text-primary-foreground/60 jobsite:italic"),
                        )}
                      >
                        {schedule.closed === true ? "Closed" : schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="jobsite:flex jobsite:justify-center">
              <SocialIcons />
            </div>
          </div>
        );
      }

      if (style === "simple") {
        return (
          <div className="jobsite:space-y-8">
            <div className="jobsite:flex jobsite:flex-col jobsite:md:flex-row jobsite:justify-between jobsite:items-start jobsite:md:items-center jobsite:gap-8">
              <div className="jobsite:flex-1">
                <div className="jobsite:flex jobsite:items-center jobsite:gap-3 jobsite:mb-2">
                  {showLogo && (
                    <img
                      src={logo}
                      alt={companyName}
                      crossOrigin="anonymous"
                      className="jobsite:h-10 jobsite:w-auto jobsite:object-contain"
                    />
                  )}
                  {showCompanyName && (
                    <h3 className={ink("jobsite:text-2xl jobsite:text-primary jobsite:font-heading", "jobsite:text-2xl jobsite:leading-tight jobsite:uppercase jobsite:text-primary-foreground jobsite:font-heading")}>
                      {companyName}
                    </h3>
                  )}
                </div>
                <p className={ink("jobsite:text-base jobsite:text-foreground/80", "jobsite:text-base jobsite:text-primary-foreground/70")}>{description}</p>
              </div>

              {showContact === "yes" && phone && (
                <div className="jobsite:flex jobsite:flex-col jobsite:gap-2">
                  {phone && (
                    <a
                      href={phoneHref(phone)}
                      className={ink("jobsite:font-semibold jobsite:hover:underline jobsite:text-primary jobsite:flex jobsite:items-center jobsite:gap-2", "jobsite:font-semibold jobsite:hover:underline jobsite:text-accent jobsite:flex jobsite:items-center jobsite:gap-2")}
                    >
                      <Phone className="jobsite:w-4 jobsite:h-4" />
                      {formatPhone(phone)}
                    </a>
                  )}
                </div>
              )}

              <SocialIcons />
            </div>

            {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
              <div className={ink("jobsite:border-t jobsite:border-border jobsite:pt-6", "jobsite:border-t jobsite:border-primary-foreground/10 jobsite:pt-6")}>
                <h4 className={ink(" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading jobsite:text-center", "jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-primary-foreground/60 jobsite:font-heading jobsite:text-center")}>
                  Business Hours
                </h4>
                <div className={ink("jobsite:flex jobsite:flex-wrap jobsite:justify-center jobsite:gap-x-8 jobsite:gap-y-2 jobsite:text-sm jobsite:text-foreground", "jobsite:flex jobsite:flex-wrap jobsite:justify-center jobsite:gap-x-8 jobsite:gap-y-2 jobsite:text-sm jobsite:text-primary-foreground/85")}>
                  {businessHours.map((schedule, idx) => (
                    <div key={idx} className="jobsite:flex jobsite:gap-2">
                      <span className="jobsite:font-medium">{schedule.day}:</span>
                      <span
                        className={cn(
                          schedule.closed && ink("jobsite:text-muted-foreground jobsite:italic", "jobsite:text-primary-foreground/60 jobsite:italic"),
                        )}
                      >
                        {schedule.closed === true ? "Closed" : schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      // Multi-column (default)
      return (
        <div className="jobsite:grid jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:lg:grid-cols-4 jobsite:gap-8">
          {/* Company Info Column */}
          <div className="jobsite:lg:col-span-1">
            {showLogo && (
              <img
                src={logo}
                alt={companyName}
                crossOrigin="anonymous"
                className="jobsite:h-10 jobsite:w-auto jobsite:object-contain jobsite:mb-4"
              />
            )}
            {showCompanyName && (
              <h3 className={ink("jobsite:text-2xl jobsite:mb-4 jobsite:text-primary jobsite:font-heading", "jobsite:text-2xl jobsite:mb-3 jobsite:leading-tight jobsite:uppercase jobsite:text-primary-foreground jobsite:font-heading")}>
                {companyName}
              </h3>
            )}
            <p className={ink("jobsite:text-sm jobsite:mb-4 jobsite:text-foreground/80", "jobsite:text-base jobsite:mb-4 jobsite:text-primary-foreground/70")}>{description}</p>
            <SocialIcons />
          </div>

          {/* Link Columns */}
          {linkColumns &&
            linkColumns.map((column, idx) => (
              <div key={idx}>
                <h4 className={ink(" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading", "jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-primary-foreground/60 jobsite:font-heading")}>
                  {column.title}
                </h4>
                <ul className="jobsite:space-y-2">
                  {column.links &&
                    column.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <a
                          href={getUrl(link)}
                          className={ink("jobsite:text-sm jobsite:text-foreground/80 jobsite:transition-colors jobsite:hover:text-primary", "jobsite:text-base jobsite:text-primary-foreground/85 jobsite:transition-colors jobsite:hover:text-accent")}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            ))}

          {/* Contact Column */}
          {showContact === "yes" && (phone || address) && (
            <div>
              <h4 className={ink(" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading", "jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-primary-foreground/60 jobsite:font-heading")}>
                Contact Us
              </h4>
              <div className={ink("jobsite:space-y-3 jobsite:text-sm jobsite:text-foreground", "jobsite:space-y-3 jobsite:text-base jobsite:text-primary-foreground/85")}>
                {phone && (
                  <p>
                    <a
                      href={phoneHref(phone)}
                      className={ink("jobsite:hover:underline jobsite:text-primary jobsite:flex jobsite:items-center jobsite:gap-2", "jobsite:font-semibold jobsite:hover:underline jobsite:text-accent jobsite:flex jobsite:items-center jobsite:gap-2")}
                    >
                      <Phone className="jobsite:w-4 jobsite:h-4" />
                      {formatPhone(phone)}
                    </a>
                  </p>
                )}
                {address && (
                  <p className={ink("jobsite:whitespace-pre-line jobsite:opacity-80 jobsite:flex jobsite:items-start jobsite:gap-2", "jobsite:whitespace-pre-line jobsite:text-primary-foreground/70 jobsite:flex jobsite:items-start jobsite:gap-2")}>
                    <MapPin className="jobsite:w-4 jobsite:h-4 jobsite:mt-0.5 jobsite:shrink-0" />
                    <span>{address}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Business Hours Column */}
          {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
            <div>
              <h4 className={ink(" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading", "jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-primary-foreground/60 jobsite:font-heading")}>
                Business Hours
              </h4>
              <div className={ink("jobsite:space-y-2 jobsite:text-sm jobsite:text-foreground", "jobsite:space-y-2 jobsite:text-sm jobsite:text-primary-foreground/85")}>
                {businessHours.map((schedule, idx) => (
                  <div key={idx} className="jobsite:flex jobsite:justify-between jobsite:gap-4">
                    <span className="jobsite:font-medium">{schedule.day}</span>
                    <span
                      className={cn(
                        "jobsite:opacity-80",
                        schedule.closed && ink("jobsite:text-muted-foreground jobsite:italic", "jobsite:text-primary-foreground/60 jobsite:italic"),
                      )}
                    >
                      {schedule.closed === true ? "Closed" : schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };

  return (
    <footer
      id={id}
      className={cn(
        ink(
          "jobsite:relative jobsite:z-40 jobsite:bg-background jobsite:border-t jobsite:border-border/70 jobsite:font-body",
          "jobsite:relative jobsite:z-40 jobsite:bg-primary jobsite:text-primary-foreground jobsite:border-t jobsite:border-primary-foreground/10 jobsite:font-body",
        ),
        className,
      )}
    >
      <div className={ink("jobsite:container jobsite:mx-auto jobsite:px-4 jobsite:py-12", cn(BOLD_CONTAINER_CLASS, "jobsite:py-12 jobsite:md:py-16"))}>
        {getFooterLayout()}
      </div>

      {/* Copyright Bar */}
      <Separator className={isBold ? "jobsite:bg-primary-foreground/10" : undefined} />
      {/* On a phone the bold footer keeps clear of the sticky call bar, so its last line can scroll into view. */}
      <div className={ink("jobsite:bg-muted/50", "jobsite:pb-[calc(env(safe-area-inset-bottom)+5rem)] jobsite:md:pb-0")}>
        <div
          className={ink(
            "jobsite:container jobsite:mx-auto jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-2 jobsite:px-4 jobsite:py-4 jobsite:sm:flex-row jobsite:sm:justify-between",
            cn(BOLD_CONTAINER_CLASS, "jobsite:flex jobsite:flex-col jobsite:items-start jobsite:gap-2 jobsite:py-5 jobsite:sm:flex-row jobsite:sm:items-center jobsite:sm:justify-between"),
          )}
        >
          {/*
            The year comes from the clock at render, not from the stored string, so a site
            built in one year still reads correctly in the next. Prerendered HTML carries the
            build-time year until hydration corrects it, which is the one legitimate mismatch.
          */}
          <p
            suppressHydrationWarning
            className={ink("jobsite:text-center jobsite:text-sm jobsite:text-foreground/70 jobsite:sm:text-left", "jobsite:text-sm jobsite:text-primary-foreground/60")}
          >
            {resolveCopyrightYear(copyrightText, new Date().getFullYear())}
          </p>
          {showServiceProBadge && (
            <a
              href={serviceProBadgeHref(businessId)}
              target="_blank"
              rel="noopener noreferrer"
              className={ink("jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:text-xs jobsite:text-foreground/60 jobsite:transition-colors jobsite:hover:text-foreground/80", "jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:text-xs jobsite:text-primary-foreground/60 jobsite:transition-colors jobsite:hover:text-primary-foreground/85")}
            >
              <span>Powered by</span>
              <svg
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
                className="jobsite:h-4 jobsite:w-4"
                aria-hidden="true"
              >
                <g transform="matrix(0.0902174,0,0,0.180435,48.5,-366.5)">
                  <rect x="0" y="2300" width="4600" height="2300" fill={BRAND.yellow} />
                </g>
                <g transform="matrix(0.288514,0,0,0.288514,-148.432,-734.842)">
                  <path
                    d="M1662.39,3121.57L1652.63,3212.85L1910.52,3153.24L1931.7,2928.19L967.124,2928.19L908.72,3548.7L1622.42,3548.7L1135.2,3738L1144.96,3646.73L894.035,3704.72L871.852,3940.4L1836.43,3940.4L1895.68,3310.87L1175.17,3310.87L1662.39,3121.57Z"
                    fill="#231F20"
                  />
                </g>
              </svg>
              <span className="jobsite:font-semibold">ServicePro.co</span>
            </a>
          )}
        </div>
      </div>
    </footer>
  );
};

export type { FooterLink, BusinessHours, FooterProps };
export { Footer };
